import React, { createContext, useContext, useState, useEffect } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import {
  DEFAULT_FILTER_DRAWER_STATUS,
  FilterDrawerStatus,
  LOCAL_STORAGE_KEY_FILTER_DRAWER_STATUS,
} from '../constants'

interface DrawerContextType {
  drawerOpen: boolean
  setDrawerOpen: (open: boolean) => void
  drawerKeepOpen: boolean
  setDrawerKeepOpen: (open: boolean) => void
}

export const useDrawerState = (): DrawerContextType => {
  const [drawerKeepOpenLocalStorage, setDrawerKeepOpenLocalStorage] =
    useLocalStorage(
      LOCAL_STORAGE_KEY_FILTER_DRAWER_STATUS,
      DEFAULT_FILTER_DRAWER_STATUS,
    )

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerKeepOpen, setDrawerKeepOpen] = useState(
    drawerKeepOpenLocalStorage === FilterDrawerStatus.EXPANDED,
  )

  return {
    drawerOpen,
    setDrawerOpen,
    drawerKeepOpen,
    setDrawerKeepOpen: (isOpen: boolean) => {
      setDrawerKeepOpen(isOpen)
      setDrawerKeepOpenLocalStorage(
        isOpen ? FilterDrawerStatus.EXPANDED : FilterDrawerStatus.COLLAPSED,
      )
    },
  }
}

const DrawerStateContext = createContext<DrawerContextType | null>(null)

export const DrawerStateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const drawerState = useDrawerState()

  return (
    <DrawerStateContext.Provider value={drawerState}>
      {children}
    </DrawerStateContext.Provider>
  )
}

export const useDrawerContext = (): DrawerContextType => {
  const context = useContext(DrawerStateContext)
  if (context === null) {
    throw new Error(
      'useDrawerContext must be used within a DrawerStateProvider',
    )
  }
  return context
}
